/**
 * PromissoryNoteList.jsx — Muhasebe FAZ 4.8, senet listesi (salt-okuma).
 *
 * FAZ 3'te PromissoryNotesSubTab'ın gövdesiydi; FAZ 4 ile sekme kabuğu
 * aksiyon formlarına ayrıldığı için liste buraya taşındı. Davranış aynı,
 * iki ekleme var:
 *   - Parçalanmış senetlerde "SN-000123 (1/3)" etiketi (split_index/total).
 *   - `splitting`/`merging` (işlem yarıda kaldı) senetlerde satırın altında
 *     "muhasebe mutabakatı gerekli" uyarısı.
 */
import { FileSignature } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { EmptyState, StatusBadge } from '../../../components/admin';
import { fmtMoneyAccounting } from '../../../utils/money';
import { promissoryStatusBadgeProps } from './columnDefs';
import { isStuckNote, splitLabel } from './promissoryMath';

export default function PromissoryNoteList({ notes }) {
  const { t, lang } = useLanguage();

  if (!notes || notes.length === 0) {
    return (
      <EmptyState
        icon={<FileSignature size={40} strokeWidth={1.5} />}
        title={t('accounting.promissory_notes.empty_title')}
        description={t('accounting.promissory_notes.empty_description')}
      />
    );
  }

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US') : '-');

  return (
    <ul className="space-y-2">
      {notes.map((note, i) => {
        const { variant, label, icon } = promissoryStatusBadgeProps(note.status, t);
        const stuck = isStuckNote(note.status);
        const numberLabel = splitLabel(note.note_number, note.split_index, note.split_total);
        return (
          <li
            key={note.note_id || i}
            className={`rounded-xl border-2 ${stuck ? 'border-rose-300 bg-rose-50/50' : 'border-[var(--border-light)] hover:bg-gray-50'}`}
            data-testid={`promissory-note-row-${i}`}
          >
            <div className="flex items-center gap-3 p-3">
              <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <FileSignature size={16} className="text-indigo-600" aria-hidden />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm">
                  {numberLabel || (
                    <span className="font-normal italic text-[var(--text-muted)]">
                      {t('accounting.promissory_notes.no_number')}
                    </span>
                  )}
                </p>
                <p className="text-[10px] text-[var(--text-muted)]">
                  {t('accounting.summary.due_label')}: {fmtDate(note.due_date)}
                  {note.description ? ` · ${note.description}` : ''}
                </p>
              </div>
              <p className="font-bold text-sm tabular-nums">{fmtMoneyAccounting(note.amount, lang)}</p>
              <StatusBadge variant={variant} label={label} icon={icon} dot />
            </div>
            {stuck && (
              <p
                role="alert"
                className="px-3 pb-2.5 text-[11px] font-bold text-rose-700"
                data-testid={`promissory-note-stuck-${i}`}
              >
                {t('accounting.notes.reconcile_title')} — {t('accounting.notes.reconcile_desc')}
              </p>
            )}
          </li>
        );
      })}
    </ul>
  );
}
